import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTheme } from '@/hooks/use-theme.hook';
import { cn } from '@/lib/utils';
import {
  NotificationPriority,
  NotificationType,
  NOTIFICATIONS_QUERY_ARGS,
  useClearAllNotificationsMutation,
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
} from '@/store/features/notifications.feature';
import { useUser } from '@clerk/react';
import { useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  iconBell,
  iconBellSlash,
  iconBox,
  iconCircleCheck,
  iconCircleExclamation,
  iconCircleInfo,
  iconCreditCard,
  iconGear,
  iconPlugCircleXmark,
  iconSpinnerThird,
  iconTrash,
  iconTriangleExclamation,
  iconXmark,
} from '@/lib/icons';

/**
 * Parse a timestamp string ensuring UTC interpretation.
 * Backend may return ISO strings without a trailing 'Z',
 * which `new Date()` treats as local time - causing "in X hours" instead of "X ago".
 */
function parseUtcTimestamp(timestamp: string): Date {
  // If it already has timezone info (Z, +, or - offset), parse as-is
  if (/[Zz]$/.test(timestamp) || /[+-]\d{2}:\d{2}$/.test(timestamp)) {
    return parseISO(timestamp);
  }
  // Otherwise treat as UTC
  return parseISO(timestamp + 'Z');
}

/**
 * The bell on the tabbed sidebar: a plain 28px button labelled by a tooltip,
 * with the menu opening to its right. The caller provides the
 * TooltipProvider.
 */
export const NotificationsDropdown = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Only fetch notifications if user has completed onboarding
  const hasCompletedOnboarding =
    user?.unsafeMetadata?.completed_onboarding === true;

  // currentData is the cached list itself, so a failed write's restore shows
  // at once. `data` keeps the last rendered (optimistic) list while the
  // refetch after the failure is in flight, and after it fails too.
  const { currentData: notificationsData, isLoading: isLoadingNotifications } =
    useGetNotificationsQuery(NOTIFICATIONS_QUERY_ARGS, {
      skip: !hasCompletedOnboarding,
      pollingInterval: 30_000,
      skipPollingIfUnfocused: true,
    });

  const [markAsRead] = useMarkNotificationsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsReadMutation();
  const [clearAll] = useClearAllNotificationsMutation();

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead({ notification_ids: [notificationId] }).unwrap();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!notificationsData) return;

    // The badge counts unread past the 20 loaded rows too, so the server
    // marks them all, even when every loaded row is already read.
    const hasUnread =
      notificationsData.unread_count > 0 ||
      notificationsData.notifications.some((n) => !n.is_read);
    if (!hasUnread) return;

    try {
      await markAllAsRead().unwrap();
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAll().unwrap();
    } catch {
      toast.error('Failed to clear notifications');
    }
  };

  const getNotificationIcon = (
    type: NotificationType,
    priority: NotificationPriority
  ) => {
    if (
      priority === NotificationPriority.URGENT ||
      priority === NotificationPriority.HIGH
    ) {
      return (
        <AppIcon
          icon={iconCircleExclamation}
          className='text-destructive'
          style={{ fontSize: 16 }}
        />
      );
    }

    switch (type) {
      case NotificationType.LOW_CREDITS:
      case NotificationType.CREDITS_DEPLETED:
        return (
          <AppIcon
            icon={iconTriangleExclamation}
            className='text-yellow-500'
            style={{ fontSize: 16 }}
          />
        );
      case NotificationType.ENRICHMENT_COMPLETE:
        return (
          <AppIcon
            icon={iconCircleCheck}
            className='text-green-500'
            style={{ fontSize: 16 }}
          />
        );
      case NotificationType.ENRICHMENT_FAILED:
        return (
          <AppIcon
            icon={iconXmark}
            className='text-destructive'
            style={{ fontSize: 16 }}
          />
        );
      case NotificationType.SUBSCRIPTION_RENEWED:
      case NotificationType.PAYMENT_FAILED:
        return (
          <AppIcon
            icon={iconCreditCard}
            className='text-primary'
            style={{ fontSize: 16 }}
          />
        );
      case NotificationType.NEW_FEATURE:
        return (
          <AppIcon
            icon={iconBox}
            className='text-purple-500'
            style={{ fontSize: 16 }}
          />
        );
      case NotificationType.SYNC_FAILED:
        // CRM disconnected / push blocked — an error/disconnect icon, not the
        // generic circle-info fallback.
        return (
          <AppIcon
            icon={iconPlugCircleXmark}
            className='text-destructive'
            style={{ fontSize: 16 }}
          />
        );
      case NotificationType.SYNC_COMPLETED:
        return (
          <AppIcon
            icon={iconCircleCheck}
            className='text-green-500'
            style={{ fontSize: 16 }}
          />
        );
      default:
        return (
          <AppIcon
            icon={iconCircleInfo}
            className='text-muted-foreground'
            style={{ fontSize: 16 }}
          />
        );
    }
  };

  const unreadCount = notificationsData?.unread_count || 0;
  const notifications = notificationsData?.notifications || [];

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type='button'
        aria-label='Notifications'
        className={cn(
          'relative grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors',
          'text-gray-600 hover:bg-black/[0.05] dark:text-gray-400 dark:hover:bg-white/[0.06]',
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50'
        )}
      >
        <AppIcon icon={iconBell} style={{ fontSize: 15 }} />
        {unreadCount > 0 && (
          <span className='absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-bold text-destructive-foreground dark:bg-red-700'>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side='top' sideOffset={6}>
          Notifications
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align='end'
        side='right'
        className={cn(
          'w-96 overflow-hidden p-0',
          isDark ? 'bg-[#1E1E1E]' : 'bg-white',
          'border',
          isDark ? 'border-white/8' : 'border-black/4',
          'shadow-2xl'
        )}
      >
        <div className='p-4 pb-2'>
          <div className='flex items-center justify-between'>
            <h3 className='text-sm font-semibold'>Notifications</h3>
            <div className='flex items-center gap-2'>
              {notifications.length > 0 && (
                <>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleMarkAllAsRead}
                    className='h-8 px-2 text-xs'
                  >
                    Mark all read
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleClearAll}
                    className='h-8 px-2'
                    aria-label='Clear all notifications'
                  >
                    <AppIcon icon={iconTrash} style={{ fontSize: 14 }} />
                  </Button>
                </>
              )}
              <Button
                variant='ghost'
                size='sm'
                className='h-8 px-2'
                aria-label='Notification settings'
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  navigate({
                    to: '/settings/notifications',
                    search: { unsubscribe: undefined },
                  });
                }}
              >
                <AppIcon icon={iconGear} style={{ fontSize: 14 }} />
              </Button>
            </div>
          </div>
        </div>
        <DropdownMenuSeparator />
        <ScrollArea className='h-[400px]'>
          {isLoadingNotifications ? (
            <div className='flex justify-center py-8'>
              <AppIcon
                icon={iconSpinnerThird}
                className='animate-spin'
                style={{ fontSize: 20 }}
              />
            </div>
          ) : notifications.length === 0 ? (
            <div className='flex flex-col items-center justify-center px-4 py-12 text-center'>
              <AppIcon
                icon={iconBellSlash}
                className='mb-4 text-muted-foreground'
                style={{ fontSize: 48 }}
              />
              <p className='text-sm text-muted-foreground'>
                No notifications yet
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                You'll see important updates here
              </p>
            </div>
          ) : (
            <div className='divide-y'>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'cursor-pointer p-4 transition-colors hover:bg-muted/50',
                    !notification.is_read && 'bg-muted/30'
                  )}
                  onClick={() => {
                    if (!notification.is_read) {
                      handleMarkAsRead(notification.id);
                    }
                    if (notification.action_url) {
                      window.location.href = notification.action_url;
                    }
                  }}
                >
                  <div className='flex min-w-0 gap-3'>
                    <div className='mt-0.5 shrink-0'>
                      {getNotificationIcon(
                        notification.notification_type,
                        notification.priority
                      )}
                    </div>
                    <div className='min-w-0 flex-1 space-y-1'>
                      <p className='text-sm font-medium wrap-break-word'>
                        {notification.title}
                      </p>
                      <p className='text-xs break-all whitespace-pre-wrap text-muted-foreground'>
                        {notification.message}
                      </p>
                      <div className='flex items-center justify-between'>
                        <p className='text-xs text-muted-foreground'>
                          {formatDistanceToNow(
                            parseUtcTimestamp(notification.created_at),
                            { addSuffix: true }
                          )}
                        </p>
                        {notification.action_url &&
                          notification.action_text && (
                            <a
                              href={notification.action_url}
                              className='text-xs text-primary hover:underline'
                            >
                              {notification.action_text}
                            </a>
                          )}
                      </div>
                    </div>
                    {!notification.is_read && (
                      <div className='mt-2 h-2 w-2 shrink-0 rounded-full bg-primary' />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
